/**
 * FreeAgent API
 * 
 * Business methods for FreeAgent operations.
 * Accepts/returns FreeAgent resource URLs as identifiers.
 * No JARVIS-specific logic - pure FreeAgent API wrapper.
 */

import { freeAgentHttpClient } from './httpClient';

export interface FreeAgentContact {
  url: string;
  first_name: string;
  last_name: string;
  organisation_name?: string;
  email?: string;
  phone_number?: string;
  address1?: string;
  address2?: string;
  address3?: string;
  town?: string;
  region?: string;
  postcode?: string;
  country?: string;
}

export interface FreeAgentInvoice {
  url: string;
  contact: string; // Contact URL
  dated_on: string; // YYYY-MM-DD
  due_on?: string; // YYYY-MM-DD
  reference?: string;
  currency: string;
  exchange_rate?: number;
  net_value: string;
  involves_sales_tax?: boolean; // Whether sales tax applies
  sales_tax_value: string;
  total_value: string;
  status: 'Draft' | 'Open' | 'Overdue' | 'Paid' | 'Written Off' | 'Cancelled';
  comments?: string;
  invoice_items: FreeAgentInvoiceItem[];
}

export interface FreeAgentInvoiceItem {
  description: string;
  item_type: 'Services' | 'Products' | 'Hours' | 'Days';
  price: number;
  quantity: number;
  sales_tax_rate: number; // VAT rate as percentage number (e.g., 20 for 20%, 5 for 5%, 0 for zero-rated)
  /** FreeAgent accepts TAXABLE or EXEMPT on invoice items. Omitted means taxable. */
  sales_tax_status?: 'TAXABLE' | 'EXEMPT';
}

/** Invoice VAT status for reporting. `Reverse Charge` covers the domestic reverse charge (dated 1/1/2021 or later). */
export type FreeAgentEcStatus = 'UK/Non-EC' | 'Reverse Charge';

export interface FreeAgentInvoiceResponse {
  invoice: FreeAgentInvoice;
}

export interface FreeAgentContactResponse {
  contact: FreeAgentContact;
}

/**
 * Ensure a contact exists in FreeAgent (create or find existing)
 * Returns the contact URL
 * 
 * Matches FreeAgent API spec:
 * POST /v2/contacts
 * Body: { "contact": { "first_name": "...", "last_name": "...", "email": "...", "address1": "...", "address2": "...", ... } }
 */
export async function ensureContact(contactData: {
  firstName: string;
  lastName: string;
  email: string;
  address?: string | null;
  billingAddress?: string | null;
}): Promise<string> {
  // Try to find existing contact by email first
  try {
    const searchResponse = await freeAgentHttpClient.request<{ contacts: FreeAgentContact[] }>(
      `/v2/contacts?view=all&search=${encodeURIComponent(contactData.email)}`
    );
    
    if (searchResponse.contacts && searchResponse.contacts.length > 0) {
      // Find a contact that matches both email AND name (to avoid wrong matches)
      const exactMatch = searchResponse.contacts.find(
        contact =>
          contact.email?.toLowerCase() === contactData.email.toLowerCase() &&
          contact.first_name?.toLowerCase() === contactData.firstName.toLowerCase() &&
          contact.last_name?.toLowerCase() === contactData.lastName.toLowerCase()
      );
      
      if (exactMatch) {
        console.log('✅ Found exact matching FreeAgent contact:', {
          url: exactMatch.url,
          firstName: exactMatch.first_name,
          lastName: exactMatch.last_name,
          email: exactMatch.email,
        });
        return exactMatch.url;
      }
      
      // If no exact match, DON'T use a partial match - create a new contact instead
      // This prevents wrong customer associations when emails are shared or similar
      console.warn('⚠️ Found contact(s) by email but name doesn\'t match exactly. Creating new contact to avoid wrong association:', {
        foundContacts: searchResponse.contacts.map(c => ({
          url: c.url,
          firstName: c.first_name,
          lastName: c.last_name,
          email: c.email,
        })),
        expectedContact: {
          firstName: contactData.firstName,
          lastName: contactData.lastName,
          email: contactData.email,
        },
      });
      // Don't return the first match - continue to create a new contact
    }
  } catch (error) {
    // Search failed, continue to create
    console.warn('Contact search failed, creating new contact:', error);
  }

  // Create new contact (matches FreeAgent API spec exactly)
  console.log('🆕 Creating new FreeAgent contact:', {
    firstName: contactData.firstName,
    lastName: contactData.lastName,
    email: contactData.email,
    address: contactData.address,
    billingAddress: contactData.billingAddress,
  });

  const contactPayload: { contact: any } = {
    contact: {
      first_name: contactData.firstName,
      last_name: contactData.lastName,
      email: contactData.email,
      sales_tax_status: 'UK',
      country: 'United Kingdom',
    },
  };

  // Map JARVIS single-line address fields to FreeAgent address lines
  // We keep this simple and reliable: primary address -> address1, billing address -> address2
  if (contactData.address) {
    contactPayload.contact.address1 = contactData.address;
  }
  if (contactData.billingAddress) {
    contactPayload.contact.address2 = contactData.billingAddress;
  }

  const response = await freeAgentHttpClient.request<FreeAgentContactResponse>(
    '/v2/contacts',
    {
      method: 'POST',
      body: contactPayload,
    }
  );

  console.log('✅ Created new FreeAgent contact:', response.contact.url);
  return response.contact.url;
}

/**
 * Contact payload for a business (directory account) rather than a named person.
 * FreeAgent needs organisation_name when first/last name are absent.
 */
export function buildOrganisationContactPayload(contactData: {
  organisationName: string;
  contactName?: string | null;
  email?: string | null;
  address?: string | null;
  vatNumber?: string | null;
}): { contact: Record<string, string> } {
  const contact: Record<string, string> = {
    organisation_name: contactData.organisationName.trim(),
    sales_tax_status: 'UK',
    country: 'United Kingdom',
  };
  const names = String(contactData.contactName || '').trim().split(/\s+/).filter(Boolean);
  if (names.length > 0) {
    contact.first_name = names[0];
    if (names.length > 1) contact.last_name = names.slice(1).join(' ');
  }
  const email = String(contactData.email || '').trim();
  if (email) contact.email = email;
  const vatNumber = String(contactData.vatNumber || '').trim();
  if (vatNumber) contact.sales_tax_registration_number = vatNumber;
  const lines = String(contactData.address || '')
    .split(/\r?\n|,/)
    .map((line) => line.trim())
    .filter(Boolean);
  ['address1', 'address2', 'address3'].forEach((field, index) => {
    if (lines[index]) contact[field] = lines[index];
  });
  if (lines.length > 3) contact.address3 = lines.slice(2).join(', ');
  return { contact };
}

/**
 * Find a FreeAgent contact whose organisation name matches exactly, or create one.
 * Returns the contact URL.
 */
export async function ensureOrganisationContact(contactData: {
  organisationName: string;
  contactName?: string | null;
  email?: string | null;
  address?: string | null;
  vatNumber?: string | null;
}): Promise<string> {
  const wanted = contactData.organisationName.trim().toLowerCase();
  try {
    const searchResponse = await freeAgentHttpClient.request<{ contacts: FreeAgentContact[] }>(
      `/v2/contacts?view=all&search=${encodeURIComponent(contactData.organisationName.trim())}`
    );
    const match = (searchResponse.contacts || []).find(
      (contact) => String(contact.organisation_name || '').trim().toLowerCase() === wanted
    );
    if (match) return match.url;
  } catch (error) {
    console.warn('FreeAgent organisation contact search failed, creating a new contact:', error);
  }

  const response = await freeAgentHttpClient.request<FreeAgentContactResponse>(
    '/v2/contacts',
    {
      method: 'POST',
      body: buildOrganisationContactPayload(contactData),
    }
  );
  return response.contact.url;
}

/**
 * Create a draft invoice in FreeAgent
 * Returns the invoice URL
 * 
 * Matches FreeAgent API spec:
 * POST /v2/invoices
 * Body: { "invoice": { "contact": "...", "dated_on": "...", "payment_terms_in_days": 14, "reference": "...", "currency": "GBP", "invoice_items": [...], "net_value": "...", "involves_sales_tax": true, "sales_tax_value": "...", "total_value": "..." } }
 * 
 * Note: We explicitly set net_value, involves_sales_tax, sales_tax_value, and total_value to ensure all totals are correctly displayed.
 */
export async function createInvoiceDraft(invoiceData: {
  contactUrl: string;
  datedOn: string; // YYYY-MM-DD
  dueOn?: string; // YYYY-MM-DD (optional, will be calculated from datedOn + paymentTermsInDays if not provided)
  paymentTermsInDays?: number; // Default 14 for UK (used if dueOn not provided)
  reference?: string;
  currency?: string;
  items: FreeAgentInvoiceItem[];
  netValue?: number; // Net value (subtotal before VAT)
  involvesSalesTax?: boolean; // Whether sales tax applies
  salesTaxValue?: number; // Total VAT amount
  totalValue?: number; // Gross total (net + VAT)
  ecStatus?: FreeAgentEcStatus;
}): Promise<string> {
  const sanitizeTaxRate = (rate: number): number => {
    const n = Number(rate);
    if (!Number.isFinite(n) || n <= 0) return 0;
    const supported = [0, 5, 20];
    return supported.reduce((best, candidate) =>
      Math.abs(candidate - n) < Math.abs(best - n) ? candidate : best
    , supported[0]);
  };
  const invoicePayload: any = {
    invoice: {
      contact: invoiceData.contactUrl,
      dated_on: invoiceData.datedOn,
      reference: invoiceData.reference,
      currency: invoiceData.currency || 'GBP',
      invoice_items: invoiceData.items.map(item => ({
        description: item.description,
        item_type: item.item_type,
        price: item.price,
        quantity: item.quantity,
        sales_tax_rate: sanitizeTaxRate(item.sales_tax_rate),
        ...(item.sales_tax_status ? { sales_tax_status: item.sales_tax_status } : {}),
      })),
      ...(invoiceData.ecStatus ? { ec_status: invoiceData.ecStatus } : {}),
    },
  };

  // payment_terms_in_days is the source of truth, including 0 (due on receipt).
  const terms = invoiceData.paymentTermsInDays;
  if (typeof terms === 'number' && Number.isFinite(terms) && terms >= 0) {
    invoicePayload.invoice.payment_terms_in_days = Math.floor(terms);
    if (invoiceData.dueOn) {
      invoicePayload.invoice.due_on = invoiceData.dueOn;
    } else {
      const [year, month, day] = invoiceData.datedOn.split('-').map(Number);
      const due = new Date(year, month - 1, day);
      due.setDate(due.getDate() + Math.floor(terms));
      const yyyy = due.getFullYear();
      const mm = String(due.getMonth() + 1).padStart(2, '0');
      const dd = String(due.getDate()).padStart(2, '0');
      invoicePayload.invoice.due_on = `${yyyy}-${mm}-${dd}`;
    }
  } else if (invoiceData.dueOn) {
    invoicePayload.invoice.due_on = invoiceData.dueOn;
    const [iy, im, id] = invoiceData.datedOn.split('-').map(Number);
    const [dy, dm, dd] = invoiceData.dueOn.split('-').map(Number);
    const invoiceDate = new Date(iy, im - 1, id);
    const dueDate = new Date(dy, dm - 1, dd);
    const daysDiff = Math.round((dueDate.getTime() - invoiceDate.getTime()) / (1000 * 60 * 60 * 24));
    invoicePayload.invoice.payment_terms_in_days = daysDiff >= 0 ? daysDiff : 7;
  }

  // Note: FreeAgent calculates totals from invoice_items automatically
  // We don't set explicit totals on creation - let FreeAgent calculate from line items
  // Then we update with explicit totals after creation if needed
  // This avoids FreeAgent ignoring our explicit values during creation

  // Debug: Log the payload being sent
  console.log('FreeAgent createInvoiceDraft payload:', JSON.stringify(invoicePayload, null, 2));

  try {
    const response = await freeAgentHttpClient.request<FreeAgentInvoiceResponse>(
      '/v2/invoices',
      {
        method: 'POST',
        body: invoicePayload,
      }
    );

    // Debug: Log the response
    console.log('FreeAgent createInvoiceDraft response:', {
      url: response.invoice.url,
      net_value: response.invoice.net_value,
      sales_tax_value: response.invoice.sales_tax_value,
      total_value: response.invoice.total_value,
      involves_sales_tax: response.invoice.involves_sales_tax,
    });

    return response.invoice.url;
  } catch (error: any) {
    // Log detailed error information for 422 validation errors
    if (error.status === 422) {
      const validationMessages: string[] = Array.isArray(error.details?.errors)
        ? error.details.errors.map((entry: any) => {
            if (typeof entry === 'string') return entry;
            if (entry && typeof entry === 'object') {
              const field = String(entry.field || entry.attribute || '').trim();
              const message = String(entry.message || entry.error || '').trim();
              return [field, message].filter(Boolean).join(': ');
            }
            return String(entry);
          }).filter(Boolean)
        : [];
      console.error('❌ FreeAgent validation error (422):', {
        message: error.message,
        code: error.code,
        details: error.details,
        validationMessages,
        payload: invoicePayload,
      });
      // Extract validation errors from FreeAgent response
      if (error.details?.errors) {
        console.error('FreeAgent validation errors:', error.details.errors);
      }
      if (validationMessages.length > 0) {
        throw {
          ...error,
          message: `FreeAgent validation failed: ${validationMessages.join(' | ')}`,
        };
      }
    }
    throw error;
  }
}

/**
 * Update an invoice in FreeAgent
 * Used to set totals explicitly after creation if FreeAgent calculated them incorrectly
 */
export async function updateInvoice(invoiceUrl: string, updates: {
  netValue?: number;
  involvesSalesTax?: boolean;
  salesTaxValue?: number;
  totalValue?: number;
}): Promise<void> {
  // Extract invoice ID from URL
  const invoiceId = invoiceUrl.split('/').pop();
  if (!invoiceId) {
    throw new Error(`Invalid invoice URL: ${invoiceUrl}`);
  }

  const updatePayload: any = {
    invoice: {},
  };

  // FreeAgent API field definitions:
  // - net_value: Net value (subtotal before VAT)
  // - involves_sales_tax: true if sales tax applies, false otherwise
  // - sales_tax_value: Total value of sales tax (VAT amount)
  // - total_value: Gross value (net + VAT)
  if (updates.netValue !== undefined) {
    updatePayload.invoice.net_value = updates.netValue.toFixed(2);
  }
  if (updates.involvesSalesTax !== undefined) {
    updatePayload.invoice.involves_sales_tax = updates.involvesSalesTax;
  }
  if (updates.salesTaxValue !== undefined) {
    updatePayload.invoice.sales_tax_value = updates.salesTaxValue.toFixed(2);
  }
  if (updates.totalValue !== undefined) {
    updatePayload.invoice.total_value = updates.totalValue.toFixed(2);
  }

  // Debug: Log the update payload
  console.log('FreeAgent updateInvoice payload:', JSON.stringify(updatePayload, null, 2));

  try {
    const response = await freeAgentHttpClient.request<FreeAgentInvoiceResponse>(
      `/v2/invoices/${invoiceId}`,
      {
        method: 'PUT',
        body: updatePayload,
      }
    );

    // Debug: Log the response
    console.log('FreeAgent updateInvoice response:', {
      url: response.invoice.url,
      net_value: response.invoice.net_value,
      sales_tax_value: response.invoice.sales_tax_value,
      total_value: response.invoice.total_value,
      involves_sales_tax: response.invoice.involves_sales_tax,
    });
  } catch (error: any) {
    // Log detailed error information for 422 validation errors
    if (error.status === 422) {
      console.error('❌ FreeAgent update validation error (422):', {
        message: error.message,
        code: error.code,
        details: error.details,
        payload: updatePayload,
        invoiceUrl,
      });
      // Extract validation errors from FreeAgent response
      if (error.details?.errors) {
        console.error('FreeAgent update validation errors:', error.details.errors);
      }
    } else {
      console.error('❌ Failed to update FreeAgent invoice:', error);
    }
    throw error;
  }
}

/**
 * Mark an invoice as sent in FreeAgent
 * Uses the invoice URL as identifier
 * 
 * FreeAgent uses transition endpoints, not direct status updates:
 * PUT /v2/invoices/:id/transitions/mark_as_sent (no body required)
 * 
 * Idempotent: if invoice is already "Open" (sent), this is a no-op
 */
export async function markInvoiceSent(invoiceUrl: string): Promise<void> {
  // Extract invoice ID from URL (e.g., "https://api.freeagent.com/v2/invoices/123" -> "123")
  const invoiceId = invoiceUrl.split('/').pop();
  if (!invoiceId) {
    throw new Error(`Invalid invoice URL: ${invoiceUrl}`);
  }

  try {
    // Check current invoice status first (idempotent check)
    const invoice = await getInvoice(invoiceUrl);
    // Anything past Draft has already been issued (Open, Overdue, Paid, Zero Value …)
    if (invoice.status && invoice.status !== 'Draft') {
      return;
    }
  } catch (error: any) {
    // If invoice doesn't exist (404), re-throw with clearer message
    if (error.status === 404 || error.message?.includes('404')) {
      throw new Error(`Invoice not found in FreeAgent: ${invoiceUrl}. It may have been deleted.`);
    }
    // For other errors, continue to attempt transition (might be a transient error)
    console.warn('Failed to check invoice status, attempting transition anyway:', error);
  }

  try {
    await freeAgentHttpClient.request(
      `/v2/invoices/${invoiceId}/transitions/mark_as_sent`,
      {
        method: 'PUT',
        // No body required for transition endpoints
      }
    );
  } catch (error: any) {
    // The invoice exists (checked above), so this is FreeAgent refusing the transition.
    // Worded without "not found" so callers never mistake it for a deleted invoice.
    throw new Error(`FreeAgent would not mark the invoice as sent (${invoiceUrl}): ${error?.message || error}`);
  }
}

/**
 * Makes sure a paid invoice is issued in FreeAgent.
 *
 * FreeAgent has no "mark as paid" transition: an invoice becomes Paid when a bank transaction
 * is explained against it in FreeAgent. So this only issues a draft; the payment itself is
 * recorded in FreeAgent's banking. Returns FreeAgent's current status.
 */
export async function markInvoicePaid(invoiceUrl: string): Promise<string | undefined> {
  const invoice = await getInvoice(invoiceUrl).catch((error: any) => {
    if (error?.status === 404) {
      throw new Error(`Invoice not found in FreeAgent: ${invoiceUrl}. It may have been deleted.`);
    }
    throw error;
  });
  if (invoice.status === 'Draft') {
    await markInvoiceSent(invoiceUrl);
    return 'Open';
  }
  return invoice.status;
}

/**
 * Mark an invoice as cancelled in FreeAgent
 * Uses the invoice URL as identifier
 * 
 * FreeAgent uses transition endpoints:
 * PUT /v2/invoices/:id/transitions/mark_as_cancelled (no body required)
 * 
 * Idempotent: if invoice is already "Cancelled", this is a no-op
 */
export async function markInvoiceCancelled(invoiceUrl: string): Promise<void> {
  // Extract invoice ID from URL
  const invoiceId = invoiceUrl.split('/').pop();
  if (!invoiceId) {
    throw new Error(`Invalid invoice URL: ${invoiceUrl}`);
  }

  try {
    // Check current invoice status first (idempotent check)
    const invoice = await getInvoice(invoiceUrl);
    if (invoice.status === 'Cancelled') {
      // Already cancelled - no-op
      return;
    }
  } catch (error: any) {
    // If invoice doesn't exist (404), re-throw with clearer message
    if (error.status === 404 || error.message?.includes('404')) {
      throw new Error(`Invoice not found in FreeAgent: ${invoiceUrl}. It may have been deleted.`);
    }
    // For other errors, continue to attempt transition
    console.warn('Failed to check invoice status, attempting transition anyway:', error);
  }

  try {
    await freeAgentHttpClient.request(
      `/v2/invoices/${invoiceId}/transitions/mark_as_cancelled`,
      {
        method: 'PUT',
        // No body required for transition endpoints
      }
    );
  } catch (error: any) {
    if (error.status === 404 || error.message?.includes('404')) {
      throw new Error(`Invoice not found or cannot be transitioned: ${invoiceUrl}`);
    }
    throw error;
  }
}

/**
 * Get invoice details by URL
 * Handles both full URLs and relative paths
 */
export async function getInvoice(invoiceUrl: string): Promise<FreeAgentInvoice> {
  // Extract path from full URL if needed (e.g., "https://api.freeagent.com/v2/invoices/123" -> "/v2/invoices/123")
  let path = invoiceUrl;
  if (invoiceUrl.startsWith('http://') || invoiceUrl.startsWith('https://')) {
    try {
      const url = new URL(invoiceUrl);
      path = url.pathname;
    } catch {
      // If URL parsing fails, try to extract path manually
      const match = invoiceUrl.match(/\/v2\/.*$/);
      if (match) {
        path = match[0];
      }
    }
  }
  
  const response = await freeAgentHttpClient.request<FreeAgentInvoiceResponse>(path);
  return response.invoice;
}

/**
 * Get contact details by URL
 * Handles both full URLs and relative paths
 */
export async function getContact(contactUrl: string): Promise<FreeAgentContact> {
  // Extract path from full URL if needed
  let path = contactUrl;
  if (contactUrl.startsWith('http://') || contactUrl.startsWith('https://')) {
    try {
      const url = new URL(contactUrl);
      path = url.pathname;
    } catch {
      // If URL parsing fails, try to extract path manually
      const match = contactUrl.match(/\/v2\/.*$/);
      if (match) {
        path = match[0];
      }
    }
  }
  
  const response = await freeAgentHttpClient.request<FreeAgentContactResponse>(path);
  return response.contact;
}



function apiPath(resourceUrl: string): string {
  if (!/^https?:\/\//.test(resourceUrl)) return resourceUrl;
  const match = resourceUrl.match(/\/v2\/.*$/);
  return match ? match[0] : resourceUrl;
}

export interface FreeAgentCategory {
  url: string;
  description: string;
  nominal_code: string;
  allowable_for_tax?: boolean;
  tax_reporting_name?: string;
}

/**
 * Cost of sales categories, which is where subcontractor costs sit, plus the CIS for Contractors
 * categories (096-099) whichever group FreeAgent lists them under.
 */
export async function listCostOfSalesCategories(): Promise<FreeAgentCategory[]> {
  const response = await freeAgentHttpClient.request<Record<string, FreeAgentCategory[] | undefined>>(
    '/v2/categories'
  );
  const costOfSales = response.cost_of_sales_categories || [];
  const seen = new Set(costOfSales.map((category) => category.url));
  const cis = Object.values(response)
    .flatMap((group) => (Array.isArray(group) ? group : []))
    .filter((category) => /^0?9[6-9]$/.test(String(category?.nominal_code ?? '').trim()) && !seen.has(category.url));
  return [...costOfSales, ...cis];
}

/**
 * Sets a contact's CIS subcontractor details. FreeAgent copies the band onto bills
 * when they are created, so this must run before the bill is posted.
 * Fails if CIS for Contractors is not switched on in FreeAgent.
 */
export async function updateContactCis(contactUrl: string, cis: {
  band: string;
  utr?: string | null;
  verificationNumber?: string | null;
}): Promise<void> {
  const contact: Record<string, unknown> = {
    is_cis_subcontractor: true,
    cis_deduction_rate: cis.band,
  };
  if (cis.utr) contact.unique_tax_reference = cis.utr;
  if (cis.verificationNumber) contact.subcontractor_verification_number = cis.verificationNumber;
  await freeAgentHttpClient.request(apiPath(contactUrl), { method: 'PUT', body: { contact } });
}

/** POST /v2/bills. Returns the new bill's URL. */
export async function createBill(payload: { bill: Record<string, unknown> }): Promise<string> {
  const response = await freeAgentHttpClient.request<{ bill: { url: string } }>(
    '/v2/bills',
    { method: 'POST', body: payload }
  );
  return response.bill.url;
}

export interface FreeAgentBill {
  url: string;
  status?: string;
  paid_on?: string | null;
  total_value?: string;
  due_value?: string;
  cis_deduction_band?: string;
  cis_deduction_rate?: string;
  cis_deduction?: string;
  bill_items?: Array<{ url: string }>;
}

/** GET a bill with its items. CIS fields are present when any item is in a CIS category. */
export async function getBill(billUrl: string): Promise<FreeAgentBill> {
  const response = await freeAgentHttpClient.request<{ bill: FreeAgentBill }>(apiPath(billUrl));
  return response.bill;
}

/** PUT /v2/bills/:id. */
export async function updateBill(billUrl: string, payload: { bill: Record<string, unknown> }): Promise<void> {
  await freeAgentHttpClient.request(apiPath(billUrl), { method: 'PUT', body: payload });
}

/** DELETE /v2/bills/:id. FreeAgent refuses once a payment is explained against the bill. */
export async function deleteBill(billUrl: string): Promise<void> {
  await freeAgentHttpClient.request(apiPath(billUrl), { method: 'DELETE' });
}

/** Whether CIS for Contractors is switched on for the company in FreeAgent. */
export async function isCisContractor(): Promise<boolean> {
  const response = await freeAgentHttpClient.request<{ company?: { cis_contractor?: boolean } }>('/v2/company');
  return response.company?.cis_contractor === true;
}
